import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/http';
import { MaintenancePageFrame, MaintenanceTabs } from '../components/maintenance/MaintenancePageChrome';
import { DashboardAccountMenu } from '../components/dashboard';
import { MaintenanceForkliftList } from '../components/maintenance/MaintenanceForkliftList';
import { SelectedUnitRecordsPanel } from '../components/maintenance/SelectedUnitRecordsPanel';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import '../styles.css';

function dedupeById(records) {
  if (!Array.isArray(records)) return [];
  const seen = new Set();
  return records.filter((r) => {
    if (!r) return false;
    const key = r.id;
    if (key === null || key === undefined) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function MaintenancePage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const customerName = resolveActiveCustomerContext(session).customerName;

  const customerId =
    session?.customerId ?? Number(localStorage.getItem('customerId') || 0);

  const role = String(session?.role || '').toLowerCase();
  const isTechnician = role === 'technician';

  const maintenancePathBase = isTechnician
    ? '/technician/maintenance'
    : '/customer/maintenance';
  const repairPathBase = isTechnician ? '/technician/repair' : '/customer/repair';
  const operatorPathBase = isTechnician
    ? '/technician/operator-checklist'
    : '/customer/operator-checklist';

  const location = useLocation();

  const buildRecordViewState = useCallback(
    (record) => {
      const unitId = record?.unitId || '';
      const returnTo = unitId
        ? `${maintenancePathBase}?unitId=${encodeURIComponent(unitId)}`
        : `${location.pathname}${location.search || ''}`;

      return {
        returnTo,
        from: returnTo,
        customerId,
      };
    },
    [customerId, location.pathname, location.search, maintenancePathBase]
  );

  const searchParams = new URLSearchParams(location.search);
  const initialUnitId = searchParams.get('unitId') || searchParams.get('unit') || '';

  const [activeTab, setActiveTab] = useState('ACTIVE');
  const [loading, setLoading] = useState(true);
  const [forklifts, setForklifts] = useState([]);
  const [selectedForkliftId, setSelectedForkliftId] = useState(null);
  const [recordsForkliftId, setRecordsForkliftId] = useState(null);
  const [shouldScrollToSelection, setShouldScrollToSelection] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [repairRecords, setRepairRecords] = useState([]);
  const [operatorChecklists, setOperatorChecklists] = useState([]);

  const [search, setSearch] = useState('');

  const loadForklifts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/forklifts');
      const list = Array.isArray(data) ? data : [];

      const filtered = customerId
        ? list.filter((f) => Number(f.customerId) === Number(customerId))
        : list;

      setForklifts(filtered);
    } catch {
      setForklifts([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadForklifts();
  }, [loadForklifts]);

  const loadRecordsForForklift = useCallback(async (forklift) => {
    if (!forklift) {
      setMaintenanceRecords([]);
      setRepairRecords([]);
      setOperatorChecklists([]);
      return;
    }

    const forkliftId = forklift.id;

    try {
      const maintPromise = forkliftId
        ? api.get(`/api/maintenance/form/forklift/${forkliftId}`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const repairsPromise = forkliftId
        ? api.get(`/api/repairs/forklift/${forkliftId}`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const operatorPromise = forkliftId
        ? api.get(`/api/operator/checklists/forklift/${forkliftId}`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const [maintRes, repairsRes, operatorRes] = await Promise.all([
        maintPromise,
        repairsPromise,
        operatorPromise,
      ]);

      setMaintenanceRecords(dedupeById(Array.isArray(maintRes.data) ? maintRes.data : []));
      setRepairRecords(dedupeById(Array.isArray(repairsRes.data) ? repairsRes.data : []));
      setOperatorChecklists(dedupeById(Array.isArray(operatorRes.data) ? operatorRes.data : []));
    } catch {
      setMaintenanceRecords([]);
      setRepairRecords([]);
      setOperatorChecklists([]);
    }
  }, []);

  useEffect(() => {
    if (!initialUnitId || !forklifts.length) return;

    const match = forklifts.find((f) => String(f.unitId || '') === String(initialUnitId));
    if (match) {
      setSelectedForkliftId(match.id);
      setRecordsForkliftId(match.id);
      loadRecordsForForklift(match);
      setShouldScrollToSelection(true);
    }
  }, [initialUnitId, forklifts, loadRecordsForForklift]);

  const sortedForklifts = useMemo(() => {
    return [...forklifts].sort((a, b) =>
      String(a.unitId || '').localeCompare(String(b.unitId || ''), undefined, {
        numeric: true,
      })
    );
  }, [forklifts]);

  const activeForklifts = useMemo(
    () =>
      sortedForklifts.filter((f) => String(f.status || '').toUpperCase() !== 'RETIRED'),
    [sortedForklifts]
  );

  const retiredForklifts = useMemo(
    () =>
      sortedForklifts.filter((f) => String(f.status || '').toUpperCase() === 'RETIRED'),
    [sortedForklifts]
  );

  const filteredActiveForklifts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeForklifts;

    return activeForklifts.filter((f) => {
      const unitId = (f.unitId || '').toLowerCase();
      const make = (f.make || '').toLowerCase();
      const model = (f.model || '').toLowerCase();
      const serial = (f.serialNumber || '').toLowerCase();

      return unitId.includes(q) || make.includes(q) || model.includes(q) || serial.includes(q);
    });
  }, [search, activeForklifts]);

  const filteredRetiredForklifts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return retiredForklifts;

    return retiredForklifts.filter((f) => {
      const unitId = (f.unitId || '').toLowerCase();
      const make = (f.make || '').toLowerCase();
      const model = (f.model || '').toLowerCase();
      const serial = (f.serialNumber || '').toLowerCase();

      return unitId.includes(q) || make.includes(q) || model.includes(q) || serial.includes(q);
    });
  }, [search, retiredForklifts]);

  const resetSelection = () => {
    setSelectedForkliftId(null);
    setRecordsForkliftId(null);
    setMaintenanceRecords([]);
    setRepairRecords([]);
    setOperatorChecklists([]);
  };

  const selectForkliftCard = (record) => {
    setSelectedForkliftId(record.id);
    setRecordsForkliftId(null);
  };

  const openForkliftRecords = (record) => {
    setSelectedForkliftId(record.id);
    setRecordsForkliftId(record.id);
    loadRecordsForForklift(record);
    setShouldScrollToSelection(true);
  };

  useEffect(() => {
    if (!shouldScrollToSelection || !selectedForkliftId) return;

    const row = document.querySelector(`.forklift-row-${selectedForkliftId}`);
    if (row && row.scrollIntoView) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    setShouldScrollToSelection(false);
  }, [shouldScrollToSelection, selectedForkliftId]);

  const rowClassName = (record) =>
    record.id === selectedForkliftId ? 'ant-table-row-selected' : '';

  const viewRecord = useCallback((type, item, forkliftContext) => {
    const pathBase = type === 'maintenance' ? maintenancePathBase : type === 'repair' ? repairPathBase : operatorPathBase;
    navigate(`${pathBase}/view?id=${item.id}`, {
      state: buildRecordViewState(forkliftContext || item),
    });
  }, [buildRecordViewState, maintenancePathBase, navigate, operatorPathBase, repairPathBase]);

  const renderRecordsPanel = (record, closeLabel = 'Back to Forklift List') => {
    const maintForForklift = maintenanceRecords.filter((m) => {
      if (!m) return false;
      const forkliftMatch =
        m.forkliftId !== undefined && m.forkliftId !== null
          ? Number(m.forkliftId) === Number(record.id)
          : false;
      const unitMatch =
        m.unitId !== undefined && m.unitId !== null
          ? String(m.unitId || '').toUpperCase() === String(record.unitId || '').toUpperCase()
          : false;
      if (m.forkliftId !== undefined || m.unitId !== undefined) {
        return forkliftMatch || unitMatch;
      }
      return true;
    });

    const repairsForForklift = repairRecords.filter((r) => {
      if (!r) return false;
      const forkliftMatch =
        r.forkliftId !== undefined && r.forkliftId !== null
          ? Number(r.forkliftId) === Number(record.id)
          : false;
      const unitMatch =
        r.unitId !== undefined && r.unitId !== null
          ? String(r.unitId || '').toUpperCase() === String(record.unitId || '').toUpperCase()
          : false;
      if (r.forkliftId !== undefined || r.unitId !== undefined) {
        return forkliftMatch || unitMatch;
      }
      return true;
    });

    const operatorsForForklift = operatorChecklists.filter(
      (o) => Number(o.forkliftId) === Number(record.id)
    );

    return (
      <SelectedUnitRecordsPanel
        forklift={record}
        maintenanceRecords={dedupeById(maintForForklift)}
        repairRecords={dedupeById(repairsForForklift)}
        operatorChecklists={dedupeById(operatorsForForklift)}
        onViewRecord={viewRecord}
        onClose={resetSelection}
        closeLabel={closeLabel}
      />
    );
  };

  const expandedRowRender = (record) => renderRecordsPanel(record, 'Collapse records');

  const recordsForklift = recordsForkliftId
    ? forklifts.find((forklift) => Number(forklift.id) === Number(recordsForkliftId))
    : null;

  const desktopRecordsPanel = recordsForklift
    ? renderRecordsPanel(recordsForklift, 'Back to Forklift List')
    : null;

  const pageTitle = customerName;
  const pageSubtitle = 'Select a forklift to view records.';
  const mobileListHelperText = pageSubtitle;
  const mobileSelectedRecordsHelperText = 'Filter or search this unit’s records.';
  const mobileHelperText = recordsForklift
    ? mobileSelectedRecordsHelperText
    : mobileListHelperText;

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    resetSelection();
  };

  const headerActions = ({ activeCustomerName, logout, navigate, session }) => (
    <DashboardAccountMenu
      customerName={activeCustomerName}
      logout={logout}
      navigate={navigate}
      session={session}
      onOpenChange={setAccountMenuOpen}
      open={accountMenuOpen}
    />
  );


  return (
    <MaintenancePageFrame
      title="Fleet Maintenance"
      subtitle={pageSubtitle}
      mobileHelperText={mobileHelperText}
      showMobileHelperText
      loading={loading}
      shellTitle={pageTitle}
      renderHeaderActions={headerActions}
      panelActions={(
        <div className={`maintenance-page-view-actions${recordsForklift ? ' maintenance-page-view-actions-mobile-hidden' : ''}`}>
          <MaintenanceTabs
            activeTab={activeTab}
            onChange={handleTabChange}
            hideOnMobileRecordsOpen={Boolean(recordsForklift)}
          />
        </div>
      )}
    >
      {activeTab === 'ACTIVE' && (
        <MaintenanceForkliftList
          forklifts={filteredActiveForklifts}
          selectedForkliftId={selectedForkliftId}
          rowClassName={rowClassName}
          expandedRowRender={expandedRowRender}
          onSelectForklift={selectForkliftCard}
          onViewRecords={openForkliftRecords}
          search={search}
          onSearchChange={setSearch}
          onRefresh={loadForklifts}
          desktopRecordsPanel={desktopRecordsPanel}
        />
      )}

      {activeTab === 'RETIRED' && (
        <MaintenanceForkliftList
          forklifts={filteredRetiredForklifts}
          selectedForkliftId={selectedForkliftId}
          rowClassName={rowClassName}
          expandedRowRender={expandedRowRender}
          onSelectForklift={selectForkliftCard}
          onViewRecords={openForkliftRecords}
          search={search}
          onSearchChange={setSearch}
          onRefresh={loadForklifts}
          desktopRecordsPanel={desktopRecordsPanel}
        />
      )}

    </MaintenancePageFrame>
  );
}
